import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {PatientDetailsEditButtons} from '@/features/patients/components/patient-details-edit-buttons/PatientDetailsEditButtons'
import {PatientNotesField} from '@/features/patients/components/patient-notes-field/PatientNotesField'
import {usePatientEditModeView} from '@/features/patients/hooks/usePatientEditModeView'
import {usePatientFormSubmission} from '@/features/patients/hooks/usePatientFormSubmission'
import {useUpdatePatient} from '@/features/patients/services/useUpdatePatient'
import {usePatientDetailsStore} from '@/features/patients/store/usePatientDetailsStore'
import {PatientExtended, PatientNotesFormValidationSchema} from '@/features/patients/types'
import {
    getPatientDirtyFieldsFromFormValues,
    remapPatientToDefaultValues,
    remapPatientToPayload
} from '@/features/patients/utils'
import {useTranslation} from '@/translations/i18n'
import {ObjectKeys} from '@/utilities/helpers'
import {zodResolver} from '@hookform/resolvers/zod'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {NotesDetails} from '@/components/ui/notes-details/NotesDetails'
import {usePermissions} from '@/features/auth/store/store'

export const PatientDetailsNotesSection = ({patient}: {patient: PatientExtended}) => {
    const form = useForm<PatientNotesFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(PatientNotesFormValidationSchema),
        defaultValues: remapPatientToDefaultValues(patient)
    })

    return (
        <FormProvider {...form}>
            <PatientNotesSectionContent patient={patient} />
        </FormProvider>
    )
}

const PatientNotesSectionContent = ({patient}: {patient: PatientExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = usePatientEditModeView('note')
    const permissions = usePermissions()
    const setIsErrorBannerVisible = usePatientDetailsStore(state => state.setIsErrorBannerVisible)
    const form = useFormContext<PatientNotesFormValidationSchema>()

    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = usePatientFormSubmission({
        formSchema: PatientNotesFormValidationSchema
    })

    // mutation
    const updatePatient = useUpdatePatient({
        urlParams: {patientId: patient.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getPatientDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapPatientToPayload(dirtyFormValues)
            return updatePatient.mutate(parsedValues)
        },
        errors => {
            setIsErrorBannerVisible(true)
            console.error(errors)
            scrollToTop()
        }
    )

    return (
        <DetailsFieldsSection
            title={t('commons:notes')}
            isDisabled={isDisabledView}
            headingEndSlot={
                permissions?.patients?.write && (
                    <PatientDetailsEditButtons
                        groupName="note"
                        isConfirmDisabled={!ObjectKeys(form.formState.dirtyFields).length}
                        onCancel={form.reset}
                        onConfirm={handleSubmit}
                    />
                )
            }
        >
            {isEditModeEnabled ? <PatientNotesField /> : <NotesDetails notes={patient.notes} />}
        </DetailsFieldsSection>
    )
}
